import { useEffect, useState, FormEvent } from "react"
import apiClient from "../../lib/api"
import type { IAnnouncement, ILocation } from "@dc-ireland/shared"
import ImagePicker from "../../components/admin/ImagePicker"
import AdminAlerts from "../../components/admin/AdminAlerts"
import { announcementTagLabel } from "../../components/landing/landingAnnouncements"

const presetTags = ["announcement", "update", "ministry", "event", "community", "charity"]

type DraftAnnouncement = IAnnouncement & {
  draftKey: string
  slugLocked: boolean
}

const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80)

const tagsFromCategory = (category?: string) =>
  (category ?? "")
    .split(",")
    .map((tag) => tag.trim().toLowerCase())
    .filter(Boolean)

const toDraft = (item: IAnnouncement, index: number): DraftAnnouncement => ({
  ...item,
  draftKey: item.slug || `draft-${index}`,
  slugLocked: Boolean(item.slug),
})

const AdminAnnouncements = () => {
  const [items, setItems] = useState<DraftAnnouncement[]>([])
  const [locations, setLocations] = useState<ILocation[]>([])
  const [tagDrafts, setTagDrafts] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        const [data, locs] = await Promise.all([
          apiClient.getAnnouncements(true),
          apiClient.getLocations(),
        ])
        setItems(data.map(toDraft))
        setLocations(locs)
      } catch (err) {
        console.error(err)
        setError("Failed to load announcements")
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const updateItem = (draftKey: string, patch: Partial<DraftAnnouncement>) => {
    setItems((current) =>
      current.map((item) => (item.draftKey === draftKey ? { ...item, ...patch } : item))
    )
  }

  const setTags = (draftKey: string, tags: string[]) => {
    const unique = [...new Set(tags.map((tag) => tag.trim().toLowerCase()).filter(Boolean))]
    updateItem(draftKey, { category: unique.join(",") || "announcement" })
  }

  const addAnnouncement = () => {
    const draftKey = `draft-${Date.now()}`
    setItems((current) => [
      {
        draftKey,
        slugLocked: false,
        slug: "",
        title: "",
        excerpt: "",
        body: "",
        category: "announcement",
        linkLabel: "Read more",
        publishedAt: new Date().toISOString().slice(0, 10),
        featured: false,
        enabled: true,
      },
      ...current,
    ])
  }

  const removeAnnouncement = (draftKey: string) => {
    setItems((current) => current.filter((item) => item.draftKey !== draftKey))
    setTagDrafts((current) => {
      const next = { ...current }
      delete next[draftKey]
      return next
    })
  }

  const addCustomTag = (draftKey: string, currentTags: string[]) => {
    const tag = slugify(tagDrafts[draftKey] ?? "")
    if (!tag) return
    setTags(draftKey, [...currentTags, tag])
    setTagDrafts((current) => ({ ...current, [draftKey]: "" }))
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    setSuccess(null)

    const payload = items.map((item, index) => {
      const slug = slugify(item.slug || item.title) || `announcement-${index + 1}`
      return {
        slug,
        title: item.title.trim(),
        excerpt: item.excerpt.trim(),
        body: item.body?.trim() || undefined,
        category: item.category?.trim() || "announcement",
        chapterSlug: item.chapterSlug || undefined,
        imageUrl: item.imageUrl || undefined,
        linkUrl: item.linkUrl?.trim() || undefined,
        linkLabel: item.linkLabel?.trim() || undefined,
        publishedAt: item.publishedAt,
        featured: item.featured === true,
        enabled: item.enabled !== false,
        sortOrder: index,
      }
    })

    const incomplete = payload.find((item) => !item.title || !item.excerpt)
    if (incomplete) {
      setSaving(false)
      setError("Each announcement needs a title and a short excerpt.")
      return
    }

    const slugs = payload.map((item) => item.slug)
    if (new Set(slugs).size !== slugs.length) {
      setSaving(false)
      setError("Two announcements share the same slug. Change one of the titles or slugs.")
      return
    }

    try {
      await apiClient.updateAnnouncements(payload)
      const data = await apiClient.getAnnouncements(true)
      setItems(data.map(toDraft))
      setSuccess("Announcements updated.")
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update announcements")
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <span className="loading loading-spinner loading-md"></span>
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold">News & Announcements</h2>
          <p className="mt-1 text-secondary">
            Add, edit, or remove stories. Tags show on the news card and in the full story.
          </p>
        </div>
        <button type="button" className="btn btn-outline btn-sm" onClick={addAnnouncement}>
          Add announcement
        </button>
      </div>

      <AdminAlerts
        error={error}
        success={success}
        onClearError={() => setError(null)}
        onClearSuccess={() => setSuccess(null)}
      />

      <form onSubmit={handleSubmit} className="space-y-4">
        {items.length === 0 ? (
          <p className="text-secondary">No announcements yet. Add one above.</p>
        ) : (
          items.map((item, index) => {
            const tags = tagsFromCategory(item.category)
            const customTags = tags.filter((tag) => !presetTags.includes(tag))

            return (
              <div key={item.draftKey} className="card bg-base-100 shadow-xl">
                <div className="card-body gap-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="card-title text-lg">{item.title || "New announcement"}</h3>
                    <div className="flex items-center gap-2">
                      <span className="badge badge-outline">Order {index + 1}</span>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm text-error"
                        onClick={() => removeAnnouncement(item.draftKey)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    <label className="form-control">
                      <span className="label-text">Slug</span>
                      <input
                        className="input input-bordered input-sm"
                        value={item.slug}
                        readOnly={item.slugLocked}
                        placeholder="Generated from the title"
                        onChange={(e) =>
                          updateItem(item.draftKey, { slug: slugify(e.target.value), slugLocked: true })
                        }
                      />
                    </label>
                    <label className="form-control">
                      <span className="label-text">Chapter</span>
                      <select
                        className="select select-bordered select-sm"
                        value={item.chapterSlug ?? ""}
                        onChange={(e) =>
                          updateItem(item.draftKey, { chapterSlug: e.target.value || undefined })
                        }
                      >
                        <option value="">National (all chapters)</option>
                        {locations.map((loc) => (
                          <option key={loc.slug} value={loc.slug}>
                            {loc.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="form-control md:col-span-2">
                      <span className="label-text">Title</span>
                      <input
                        className="input input-bordered input-sm"
                        value={item.title}
                        onChange={(e) => {
                          const title = e.target.value
                          const patch: Partial<DraftAnnouncement> = { title }
                          if (!item.slugLocked) patch.slug = slugify(title)
                          updateItem(item.draftKey, patch)
                        }}
                        required
                      />
                    </label>
                    <div className="form-control md:col-span-2">
                      <span className="label-text">Tags</span>
                      <div className="mt-2 flex flex-wrap gap-3">
                        {presetTags.map((tag) => (
                          <label key={tag} className="label cursor-pointer gap-2 py-0">
                            <input
                              type="checkbox"
                              className="checkbox checkbox-primary checkbox-sm"
                              checked={tags.includes(tag)}
                              onChange={() => {
                                const next = tags.includes(tag)
                                  ? tags.filter((entry) => entry !== tag)
                                  : [...tags, tag]
                                setTags(item.draftKey, next)
                              }}
                            />
                            <span className="label-text">{announcementTagLabel(tag)}</span>
                          </label>
                        ))}
                      </div>
                      {customTags.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {customTags.map((tag) => (
                            <button
                              key={tag}
                              type="button"
                              className="badge badge-outline gap-1"
                              onClick={() =>
                                setTags(
                                  item.draftKey,
                                  tags.filter((entry) => entry !== tag)
                                )
                              }
                            >
                              {announcementTagLabel(tag)}
                              <span aria-hidden>×</span>
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="mt-3 flex flex-wrap gap-2">
                        <input
                          className="input input-bordered input-sm w-full max-w-xs"
                          value={tagDrafts[item.draftKey] ?? ""}
                          placeholder="Add a tag"
                          onChange={(e) =>
                            setTagDrafts((current) => ({ ...current, [item.draftKey]: e.target.value }))
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault()
                              addCustomTag(item.draftKey, tags)
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => addCustomTag(item.draftKey, tags)}
                        >
                          Add tag
                        </button>
                      </div>
                    </div>
                    <label className="form-control md:col-span-2">
                      <span className="label-text">Excerpt</span>
                      <textarea
                        className="textarea textarea-bordered textarea-sm"
                        rows={2}
                        value={item.excerpt}
                        onChange={(e) => updateItem(item.draftKey, { excerpt: e.target.value })}
                        required
                      />
                    </label>
                    <label className="form-control md:col-span-2">
                      <span className="label-text">Full body (modal)</span>
                      <textarea
                        className="textarea textarea-bordered textarea-sm"
                        rows={4}
                        value={item.body ?? ""}
                        onChange={(e) => updateItem(item.draftKey, { body: e.target.value })}
                      />
                    </label>
                    <div className="md:col-span-2">
                      <ImagePicker
                        label="Image"
                        value={item.imageUrl ?? ""}
                        onChange={(imageUrl) => updateItem(item.draftKey, { imageUrl })}
                      />
                    </div>
                    <label className="form-control">
                      <span className="label-text">Published date</span>
                      <input
                        type="date"
                        className="input input-bordered input-sm"
                        value={
                          item.publishedAt
                            ? new Date(item.publishedAt).toISOString().slice(0, 10)
                            : ""
                        }
                        onChange={(e) => updateItem(item.draftKey, { publishedAt: e.target.value })}
                      />
                    </label>
                    <label className="form-control">
                      <span className="label-text">Link URL</span>
                      <input
                        className="input input-bordered input-sm"
                        value={item.linkUrl ?? ""}
                        onChange={(e) => updateItem(item.draftKey, { linkUrl: e.target.value })}
                        placeholder="/give"
                      />
                    </label>
                    <label className="form-control">
                      <span className="label-text">Link label</span>
                      <input
                        className="input input-bordered input-sm"
                        value={item.linkLabel ?? ""}
                        onChange={(e) => updateItem(item.draftKey, { linkLabel: e.target.value })}
                        placeholder="Read more"
                      />
                    </label>
                  </div>

                  <div className="flex flex-wrap gap-4">
                    <label className="label cursor-pointer gap-2">
                      <input
                        type="checkbox"
                        className="checkbox checkbox-primary checkbox-sm"
                        checked={item.featured === true}
                        onChange={(e) =>
                          setItems((current) =>
                            current.map((entry) => ({
                              ...entry,
                              featured: entry.draftKey === item.draftKey ? e.target.checked : false,
                            }))
                          )
                        }
                      />
                      <span className="label-text">Featured on landing page</span>
                    </label>
                    <label className="label cursor-pointer gap-2">
                      <input
                        type="checkbox"
                        className="checkbox checkbox-primary checkbox-sm"
                        checked={item.enabled !== false}
                        onChange={(e) => updateItem(item.draftKey, { enabled: e.target.checked })}
                      />
                      <span className="label-text">Published</span>
                    </label>
                  </div>
                </div>
              </div>
            )
          })
        )}

        <div className="flex justify-end">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Saving…" : "Save Announcements"}
          </button>
        </div>
      </form>
    </div>
  )
}

export default AdminAnnouncements
